<template>
  <div
    class="task"
    :class="{
      ignored: isIgnored,
      'embedded-mode': embeddedMode,
    }"
  >
    <FindBar
      v-if="isFindBarOpen"
      ref="findBarEl"
      container-id="scrollable-document"
      @close="closeFindBar"
    />
    <div id="scrollable-document">
      <TaskTopBar
        v-if="!embeddedMode"
        :task="value"
        :has-block-active="hasBlockActive"
        @select-all="selectAll"
        @flush="flush"
        @check-task="checkTask"
        @find="openFindBar"
      />
      <div v-if="value" class="infos" @keydown="keydown">
        <TitledDocument
          ref="document"
          :key="value.id"
          class="titled-document"
          :debounce="debounce"
          :templates="templates"
          :value="value"
          :editable-title="value?.canEditTitle"
          :is-title-grayed-out="isCompleted || isIgnored"
          :loading-title="taskLoading"
          :loading-notes="taskLoading"
          :embedded-mode="embeddedMode"
        >
          <template #properties>
            <Property
              v-if="sortedAllocations && sortedAllocations.length"
              name="Allocations"
              state="full"
              class="allocations-property"
              :class="{ 'only-property': !hasExtendedProperties }"
            >
              <template #icon>
                <EventIcon />
              </template>
              <div class="allocation-list">
                <div
                  v-for="a in sortedAllocations"
                  :key="a.id"
                  class="allocation-item"
                  :class="{ past: isEventPast(a) }"
                >
                  <CalendarTag
                    :value="a.calendar"
                    display="icon-only"
                    :has-tooltip="true"
                    :has-padding="false"
                    compact
                  />
                  <DateAndTime :value="a.time" :editable="false" compact />
                  <BaseButton
                    kind="auxiliary"
                    class="delete-allocation-button"
                    tooltip="Delete"
                    tooltip-display="always"
                    @click="deleteAllocation(a.id)"
                  >
                    <template #icon>
                      <CloseIcon />
                    </template>
                  </BaseButton>
                </div>
              </div>
            </Property>
            <ExtendedProperties
              class="extension-field-list"
              objectType="task"
              :objectId="value.id"
              :extensionObjectId="value.extension_row"
              :create-extension-object-callback="ensureTaskExtensionObject"
            />
          </template>

          <template #preTitle>
            <TaskCheckbox
              ref="checkboxEl"
              class="checkbox"
              :status="{ completed: value.completed, ignored: value.ignored }"
              :is-disabled="taskLoading || isBrokenIntegration"
              size="lg"
              @click="checkTask"
            />
          </template>

          <template #aboveTitle>
            <DefaultTypeChip v-if="!embeddedMode" kind="task" />
            <ContextButton
              class="title-context-button"
              :task="value"
              :task-context="value.context"
              :integration-status="value.integrationStatus"
              is-shortcut-enabled
              show-text
            />
          </template>

          <template #subTitle>
            <EventProgressBar
              v-if="event"
              class="progress-bar"
              :event="event"
              with-remaining
            />
          </template>
        </TitledDocument>
      </div>

      <div v-else class="content">No such task.</div>

      <div v-if="integrationFailure" class="errorcontainer">
        <div class="errorbox">
          <div>
            <span>{{ integrationErrorMessage }}</span>
            <span class="error">&nbsp;{{ value.context }}.</span>
          </div>
          <BaseButton
            kind="auxiliary"
            text="See details"
            tint="red"
            @click="showIntegrationError"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useHead } from "@unhead/vue";
import { computedAsync } from "@vueuse/core";
import { isPast } from "date-fns";
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  provide,
  ref,
  toRefs,
  watch,
} from "vue";

import type { EventTime, FullEvent, FullTask } from "@ctrl/controller-rpc";

import { taskActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import DateAndTime from "@/components/DateAndTime.vue";
import { useDocument } from "@/components/document";
import CalendarTag from "@/components/elements/CalendarTag.vue";
import ContextButton from "@/components/elements/ContextButton.vue";
import DefaultTypeChip from "@/components/elements/DefaultTypeChip.vue";
import EventProgressBar from "@/components/EventProgressBar.vue";
import FindBar from "@/components/find/FindBar.vue";
import { useFindBar } from "@/components/find/useFindBar";
import Property from "@/components/graph/properties/Property.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import EventIcon from "@/components/icons/EventIcon.vue";
import * as inject from "@/components/inject";
import TaskCheckbox from "@/components/task/TaskCheckbox.vue";
import TaskTopBar from "@/components/task/TaskTopBar.vue";
import { TemplateName } from "@/components/templates";
import TitledDocument from "@/components/TitledDocument.vue";
import { useExtendedProperties } from "@/composables/useExtendedProperties";
import { useScrollable } from "@/composables/useScrollable";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { keydown } from "@/routes/object";
import { RouteNames } from "@/routes/types";
import { in_electron } from "@/utils/in";
import { navigateTo } from "@/utils/navigate";

import ExtendedProperties from "../ExtendedProperties.vue";

export default defineComponent({
  components: {
    BaseButton,
    CalendarTag,
    CloseIcon,
    DateAndTime,
    EventIcon,
    EventProgressBar,
    ContextButton,
    DefaultTypeChip,
    ExtendedProperties,
    FindBar,
    Property,
    TaskTopBar,
    TaskCheckbox,
    TitledDocument,
  },
  props: {
    debounce: {
      type: Number,
      default: 5000,
    },
    previousRoute: {
      type: String,
      default: null,
    },
    // The task screen only hosts native tasks: bound tasks (graph rows
    // surfaced as tasks) redirect to their object screen in `routes/Task.vue`.
    value: {
      type: Object as PropType<FullTask>,
      required: true,
    },
    embeddedMode: {
      type: Boolean,
      default: false,
    },
  },
  async setup(props) {
    const { value: task } = toRefs(props);
    const controller = inject.controller();
    const profile = inject.profile();
    const router = inject.router();
    const releaser = useReleaser();
    const parent = ref({ kind: "task", id: task.value.id });
    provide("parent", parent);

    const ensureTaskExtensionObject = useExtendedProperties(
      controller,
      profile,
      "task",
      () => task.value.extension_row,
      (id: string) => {
        if (task.value) {
          task.value.extension_row = id;
        }
      },
      async (id: string) => {
        await controller.task.update(
          task.value.id,
          { extension_row: id },
          "app",
          null
        );
      }
    );

    const document = ref<InstanceType<typeof TitledDocument> | null>(null);

    const { isFindBarOpen, findBarEl, openFindBar, closeFindBar } =
      useFindBar();

    const allocations = computedAsync<FullEvent[]>(async () => {
      const list = await Promise.all(
        task.value.allocations?.map(async (eventId) => {
          const event = await controller.event.get(eventId);
          event.release();
          return event.value;
        }) || []
      );
      return list.filter((e): e is FullEvent => e !== null);
    });
    const sortedAllocations = computed(() =>
      (allocations.value || [])
        .slice()
        .sort((a, b) => a.start.getTime() - b.start.getTime())
    );
    const isEventPast = (e: FullEvent) => {
      // Consider past only when the allocation's end datetime is in the past.
      // Fallback to start time if end is missing.
      const end = e.end || e.start;
      return isPast(end);
    };
    const deleteAllocation = async (eventId: string) => {
      await controller.event.delete(eventId, false, "app");
    };
    // Register the document element as a scrollable container
    const scrollable = useScrollable(["scrollable-document"]);
    onMounted(scrollable.mount);
    releaser.defer(scrollable.unmount);

    useHead({
      title: computed(() => task.value.title),
    });
    watch(task, (task) => (parent.value = { kind: "task", id: task.id }));
    const event = await releaser.add(
      bindAsync(task, async (task) =>
        task.allocations
          ? await controller.event.get(task.allocations[0])
          : releasableRef(undefined)
      )
    );
    const eventTime = computed({
      get() {
        return event.value?.time || null;
      },
      set(time: EventTime | null) {
        if (event.value && time) {
          controller.event.update(
            { id: event.value.id, time: time },
            false,
            "app"
          );
        }
      },
    });
    const closable = computed(() => {
      if (!event.value) return false;
      return !!event.value.task && !isPast(event.value.start);
    });

    const completeTask = async () => {
      if (!taskLoading.value) {
        document.value?.flush();
        task.value.completed = task.value.completed ? null : new Date();
        if (!task.value.completed)
          taskActions.unignore.action(
            { type: "task", value: task.value },
            { controller }
          );
      }
    };

    const restoreTask = async () => {
      if (!taskLoading.value) {
        document.value?.flush();
        taskActions.restore.action(
          { type: "task", value: task.value },
          { controller }
        );
      }
    };

    const checkTask = async () => {
      if (task.value.completed || task.value.ignored) {
        await restoreTask();
      } else {
        await completeTask();
      }
    };
    const taskLoading = computed(() => {
      if (!task.value.integrationStatus) {
        return false;
      }
      return task.value.integrationStatus.status === "initializing";
    });
    const integrationFailure = computed(() => {
      if (!task.value.integrationStatus) {
        return false;
      }
      return task.value.integrationStatus.status === "broken";
    });
    const integrationErrorMessage = computed(() => {
      if (!integrationFailure.value) {
        return null;
      }
      if (task.value.context === "notion" && !in_electron) {
        return "Notion does not support querying from the browser, use the native App to sync this task to";
      }
      return "Error while syncing this task to";
    });
    function showIntegrationError() {
      // Will be used by the error button.
      if (!task.value.context) {
        return;
      }

      if (task.value.context === "notion") {
        navigateTo(RouteNames.SettingsIntegrationsNotion, router);
      } else {
        navigateTo(RouteNames.SettingsIntegrations, router);
      }
    }
    const calendarId = computed({
      get() {
        return event.value?.calendar || null;
      },
      set(calendarId: string | null) {
        if (event.value && calendarId) {
          controller.event.move(event.value.id, calendarId, false, "app");
        }
      },
    });
    const templates: Array<TemplateName> = ["empty", "embed", "subtasks"];
    const isCompleted = computed<boolean>(() => !!task.value.completed);
    const isIgnored = computed(
      () => !!task.value?.ignored && !task.value?.completed
    );
    const isBrokenIntegration = computed(
      () => task.value?.integrationStatus?.status === "broken"
    );
    const hasExtendedProperties = computed(() => !!task.value.extension_row);
    return {
      allocations,
      sortedAllocations,
      isEventPast,
      deleteAllocation,
      calendarId,
      checkTask,
      closable,
      closeFindBar,
      document,
      findBarEl,
      isFindBarOpen,
      openFindBar,
      event,
      eventTime,
      integrationErrorMessage,
      integrationFailure,
      isBrokenIntegration,
      isCompleted,
      isIgnored,
      keydown,
      showIntegrationError,
      completeTask,
      restoreTask,
      taskLoading,
      templates,
      ensureTaskExtensionObject,
      hasExtendedProperties,
      ...useDocument<FullTask>(task, document, router),
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/event.scss";

.errorcontainer {
  position: absolute;
  z-index: $z-index-content;
  bottom: 24px;
  inset-inline-start: 0;
  inset-inline-end: 0;

  display: flex;
  align-items: center; // Center the thing
  justify-content: center;

  width: 100%;

  .errorbox {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    row-gap: 8px;

    border-radius: 20px;
    border: 1px solid;
    padding: 8px 24px;

    @include themed() {
      border-color: t($secondary-30);
      background-color: t($secondary-10);
    }

    .error {
      text-transform: capitalize;
      @include themed() {
        color: t($secondary-80);
      }
    }
  }
}

.task {
  // Anchor for the find bar.
  position: relative;

  #scrollable-document {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    overflow-y: scroll;
    scrollbar-width: none;
  }
  &.infos {
    .titled-document :deep(.title) .r-input .input {
      @include themed() {
        color: t($grey-30);
      }
    }
  }

  .title-context-button :deep(button.main-button) {
    padding: 4px;
    // Compensate the padding so the icon left-aligns with the task checkbox
    // below.
    margin-inline-start: -4px;
    border-radius: $border-radius-m;
    cursor: default;
    @include transition(background-color 100ms ease-out);
    &:hover {
      @include themed() {
        background-color: t($grey-10);
      }
    }
  }

  .allocations-property {
    width: 100%;
    padding-inline-start: 4px;

    &.only-property :deep(.property) {
      display: none;
    }

    .property :deep(.property-value.full) {
      padding-inline-start: 8px;
      align-items: flex-start;
    }

    .property :deep(.property-value) {
      background-color: transparent;

      &:hover:not(.focused) {
        background-color: transparent;
      }
    }

    .allocation-list {
      display: flex;
      flex-direction: column;
      row-gap: 8px;
      padding-inline-start: 8px;
      width: 100%;
    }

    .allocation-item.past {
      opacity: 0.6;
    }

    .allocation-item {
      display: flex;
      align-items: center;
      column-gap: 8px;

      .delete-allocation-button {
        opacity: 0;
        flex-shrink: 0;
        margin: 0;
        padding: 4px;
        width: 20px;
        height: 20px;
        min-width: 20px;
        min-height: 20px;
        line-height: 1;
        visibility: hidden;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;

        &.auxiliary.icon-only {
          padding: 4px;
        }

        :deep(.icon),
        :deep(svg) {
          height: 12px;
          width: 12px;
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        :deep(.left) {
          padding: 0;
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        :deep(path.fill) {
          @include themed() {
            fill: t($grey-50);
          }
        }

        &:hover {
          @include themed() {
            background: t($grey-15);
          }

          :deep(path.fill) {
            @include themed() {
              fill: t($grey-80);
            }
          }
        }
      }

      &:hover .delete-allocation-button {
        opacity: 1;
        visibility: visible;
        margin-inline-start: 4px;
      }
    }
  }

  .extension-field-list {
    margin-top: 6px;
    width: 100%;
  }

  .checkbox {
    top: 13px;
  }

  &.embedded-mode {
    .checkbox {
      position: static;
    }
  }
}

.progress-bar {
  margin-top: 12px;
}

.schedule {
  display: flex;
  flex-direction: row;
}
</style>

<style lang="scss">
.task-destination-theme {
  top: -48px;
}
</style>
